// @flow
/** @file Edit/Display Structure Address */

import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';

import { LabelValue } from '../../components/LabelValue';
import { Address as AddressForm } from '../../Structures/Create/Address';
// Types
import type { Address as AddressType } from '../../types';

// Dialog /////////////////////////////////////////////////////////////////////

const useEditDialogStyles: () => {
  wrapper: string;
} = makeStyles({
  wrapper: {
    display: 'flex',
    width: '100%',
  },
});

type Props = {
  open: boolean;
  onClose: () => void;
  address: AddressType;
  onUpdate: (AddressType) => Promise<void>;
  loading: boolean;
};

/**
 * Edit Address dialog
 *
 * @param {object} props - Component properties
 * @param {boolean} props.open - Whether or not the dialog is open
 * @param {Function} props.onClose - Handler for close action
 * @param {object} props.address - Address to edit
 * @param {Function} props.onUpdate - Update handler
 * @returns {React.Component} - Edit dialog
 */
const EditDialog = ({ open, onClose, address, onUpdate, loading = false }: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useEditDialogStyles();

  // State ////////////////////////////

  const [street, setStreet] = React.useState(address.street);
  const [street2, setStreet2] = React.useState(address.street2);
  const [city, setCity] = React.useState(address.city);
  const [state, setState] = React.useState(address.state);
  const [zip, setZip] = React.useState(address.zip);

  React.useEffect(() => {
    setStreet(address.street);
    setStreet2(address.street2);
    setCity(address.city);
    setState(address.state);
    setZip(address.zip);
  }, [address]);

  // Component ////////////////////////

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth={true}
      maxWidth={'xs'}
      aria-labelledby="structure-create-form-dialog"
      open={open}
      onClose={onClose}
    >
      <DialogTitle id="structure-create-form-dialog">
        <Typography variant={'h4'} style={{ textAlign: 'center' }}>
          {'Edit Address'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <div data-component-id={'STRUCTURE-ADDRESS-EDIT'} className={classes.wrapper}>
          <AddressForm
            title={'Edit Address'}
            street={street}
            street2={street2}
            city={city}
            state={state}
            zip={zip}
            onStreetChange={setStreet}
            onStreet2Change={setStreet2}
            onCityChange={setCity}
            onStateChange={setState}
            onZipChange={setZip}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <SWLoadingButtonOutlined onClick={onClose} color="primary">
          {'Cancel'}
        </SWLoadingButtonOutlined>
        <SWLoadingButton
          onClick={async () => {
            try {
              await onUpdate({
                street,
                street2,
                city,
                state,
                zip,
              });
              onClose();
            } catch (err) {
              // Error is already shown by the API layer, just don't close dialog
              console.error('Failed to update address:', err);
            }
          }}
          color="primary"
          autoFocus
          loading={loading}
        >
          {'Update'}
        </SWLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// Component //////////////////////////////////////////////////////////////////

const useStyles = makeStyles({
  root: {
    alignItems: 'center',
    display: 'flex',
  },
  button: {
    height: 'fit-content',
  },
  editIcon: {
    opacity: 0.3,
    paddingTop: 0,
    '&:hover': {
      opacity: 1,
    },
  },
  inputRoot: {
    fontSize: '3rem',
    fontWeight: 400,
    letterSpacing: '0em',
    lineHeight: '1.167',
  },
});

export const Address = ({
  address,
  onUpdate,
  loading = false,
}: {
  address: AddressType;
  onUpdate: (AddressType) => Promise<void>;
  loading: boolean;
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = React.useState(false);
  return (
    <div className={classes.root}>
      <LabelValue
        label={''}
        value={`${address.street} ${address.street2 || ''}, ${address.city} ${address.state} ${
          address.zip
        }`}
      />
      <IconButton size={'small'} onClick={() => setIsEditing(true)} className={classes.editIcon}>
        <EditIcon viewBox={'-4 -4 32 32'} />
      </IconButton>
      <EditDialog
        address={address}
        open={isEditing}
        onClose={() => setIsEditing(false)}
        onUpdate={onUpdate}
        loading={loading}
      />
    </div>
  );
};
